/**
 * Gaushala Profile Service
 * Handles API calls for fetching comprehensive gaushala profile data
 */

const API_BASE_URL = import.meta.env.VITE_GAUSHALA_SERVICE_URL || 'http://localhost:8086/gaushala-service';

// ============================================================================
// DATA TYPES / INTERFACES
// ============================================================================

/**
 * Person/User Details from Auth Service
 */
export interface PersonDetails {
  id?: number;
  name?: string;
  phone?: string;
  email?: string;
  locale?: string;
  kycStatus?: string;
  isActive?: boolean;
  profilePhoto?: string;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  roles?: string[];
  permissions?: string[];
  governmentAccess?: string[];
}

/**
 * Gaushala Details
 */
export interface GaushalaDetails {
  id?: number;
  gaushalaName?: string;
  registeredAddress?: string;
  registrationNumber?: string;
  organizationType?: string;
  yearEstablished?: string;
  contactNumber?: string;
  email?: string;
  website?: string;
  headName?: string;
  headMobileNumber?: string;
  headEmail?: string;
  totalCattle?: number;
  totalCows?: number;
  totalBulls?: number;
  totalCalves?: number;
  milkingCows?: number;
  avgMilkProduction?: number;
  totalLandArea?: number;
  shelterCapacity?: number;
  waterSource?: string;
  electricityAvailable?: number;
  annualBudget?: number;
  sourceOfFunds?: string;
  bankAccountDetails?: string;
  organizationPan?: string;
  gaushalaPhoto?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Cattle Statistics
 */
export interface CattleStats {
  totalCattle?: number;
  totalCows?: number;
  totalBulls?: number;
  totalCalves?: number;
  milkingCows?: number;
  avgMilkProduction?: number;
}

/**
 * Infrastructure Statistics
 */
export interface InfrastructureStats {
  totalLandArea?: number;
  shelterCapacity?: number;
  waterSource?: string;
  electricityAvailable?: number;
}

/**
 * Financial Statistics
 */
export interface FinancialStats {
  annualBudget?: number;
  sourceOfFunds?: string;
}

/**
 * Gaushala Statistics
 */
export interface GaushalaStatistics {
  cattle?: CattleStats;
  infrastructure?: InfrastructureStats;
  financial?: FinancialStats;
}

/**
 * Complete Gaushala Profile Response
 */
export interface GaushalaProfileResponse {
  personDetails?: PersonDetails;
  gaushalaDetails?: GaushalaDetails;
  statistics?: GaushalaStatistics;
}

/**
 * API Response Wrapper
 */
export interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
  totalElements?: number;
  pageNumber?: number;
  pageSize?: number;
  totalPages?: number;
}

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Get complete gaushala profile
 * Fetches person details, gaushala info, and statistics
 *
 * @returns Promise with complete gaushala profile data
 * @throws Error if API call fails
 */
export const getGaushalaProfile = async (): Promise<GaushalaProfileResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/gaushala/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Profile API Error Response:', errorData);
      throw new Error(
        errorData?.message || `API Error: ${response.status} ${response.statusText}`
      );
    }

    const result = await response.json();
    console.log('Profile API Response:', result);

    // Backend returns: { data: { personDetails, gaushalaDetails, statistics } }
    // or directly: { personDetails, gaushalaDetails, statistics }
    const profileData = result.data || result;

    // Ensure all required fields are properly structured
    // Backend may return data as-is (already properly formatted by backend)
    return {
      personDetails: profileData.personDetails || null,
      gaushalaDetails: profileData.gaushalaDetails || null,
      statistics: profileData.statistics || null,
    };
  } catch (error) {
    console.error('Error fetching gaushala profile:', error);
    throw error;
  }
};

/**
 * Upload gaushala profile photo
 *
 * @param file Image file to upload
 * @returns Promise with upload response
 */
export const uploadProfilePhoto = async (file: File): Promise<{ photoUrl: string }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/api/v1/gaushala/profile/photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error;
  }
};

/**
 * Get person details only
 *
 * @returns Promise with person details
 */
export const getPersonDetails = async (): Promise<PersonDetails> => {
  try {
    const profile = await getGaushalaProfile();
    return profile.personDetails || {};
  } catch (error) {
    console.error('Error fetching person details:', error);
    throw error;
  }
};

/**
 * Get gaushala details only
 *
 * @returns Promise with gaushala details
 */
export const getGaushalaDetails = async (): Promise<GaushalaDetails> => {
  try {
    const profile = await getGaushalaProfile();
    return profile.gaushalaDetails || {};
  } catch (error) {
    console.error('Error fetching gaushala details:', error);
    throw error;
  }
};

/**
 * Get statistics only
 *
 * @returns Promise with gaushala statistics
 */
export const getGaushalaStatistics = async (): Promise<GaushalaStatistics> => {
  try {
    const profile = await getGaushalaProfile();
    return profile.statistics || {};
  } catch (error) {
    console.error('Error fetching gaushala statistics:', error);
    throw error;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string => {
  return localStorage.getItem('saubhagya_jwt_token') || '';
};

/**
 * Format currency values
 */
export const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

/**
 * Format large numbers
 */
export const formatNumber = (value?: number): string => {
  if (value === undefined || value === null) return '0';
  return value.toLocaleString('en-IN');
};

/**
 * Format date string
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

/**
 * Format decimal values with 2 decimal places
 */
export const formatDecimal = (value?: number): string => {
  if (value === undefined || value === null) return '0.00';
  return value.toFixed(2);
};
